import { MemoryStorage } from 'services/storage';
import { StorageType } from 'types/storage';

/**
 * Check if the specified exception represents a quota exceeded DOM exception
 *
 * @param {DOMException} e Source exception
 */
const isStorageQuotaExceededException = (e: DOMException) =>
  // Everything except Firefox
  e.code === 22 ||
  // Firefox
  e.code === 1014 ||
  // Test name field too, because code might not be present (everything except Firefox)
  e.name === 'QuotaExceededError' ||
  // Firefox
  e.name === 'NS_ERROR_DOM_QUOTA_REACHED';

/**
 * Check if storage of the specified type is available
 *
 * @param {StorageType} type Target storage type
 */
const isStorageAvailable = (type: StorageType) => {
  let storage: Storage;

  try {
    // Retrieve the specified storage object
    storage = window[type];

    // Attempt to write and remove the value
    const value = '@';
    storage.setItem(value, value);
    storage.removeItem(value);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      isStorageQuotaExceededException(e) &&
      // Acknowledge QuotaExceededError only if there's something already stored
      // @ts-ignore
      storage &&
      storage.length !== 0
    );
  }
};

/**
 * Retrieve storage of the first matching type or fall back to memory storage if none is available
 *
 * @param {Array<StorageType>} types List of storage types in the order of priority
 */
export const getStorage = (...types: Array<StorageType>) => {
  // Return first supported storage object
  for (const type of types) {
    if (isStorageAvailable(type)) {
      return window[type];
    }
  }

  // Fall back to memory storage if none is found
  return new MemoryStorage();
};
